/**
 * Areas and projects, fetched independently of whatever page or card asks for them.
 *
 * The shared foundation self-contained cards build on instead of relying on a parent page's
 * own fetch — the same reason `RecurringCard`/`NotesSection` fetch their own data rather
 * than riding a payload someone else assembled. `AreasCard` and `ProjectsCard` each call
 * this on their own; two independent fetches of the same small lists costs nothing a solo
 * user would notice, and is the price of a card that works the same wherever it is mounted.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type Area, type Project } from "./api";

export function useAreasAndProjects(refreshKey?: number) {
  const [areas, setAreas] = useState<Area[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([api.areas("all"), api.projects("all")]);
      setAreas(a.areas);
      setProjects(p.projects);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  // `loaded` flips true exactly once, on the first load to settle — never back to false on
  // a later `reload()` — so a caller can snapshot "what was already done/archived before
  // this session" exactly once (gitea #49, D204) rather than on every refetch.
  return { areas, projects, error, reload: load, loaded };
}
